import { Grid, TextField, Typography } from '@mui/material';
import { useFormik } from 'formik';
import { useCallback, useEffect, useRef, useState } from 'react';
import { orderAddressCheckoutSchema } from '../validations/orderValidations';
import validateAddress, { validateZipcode } from '../utils/validations';
import useBackendApi from '../hooks/useBackendApi';
import useSnackBarAlertMessages from '../hooks/useSnackBarAlertMessages';
import SearchableDropdown from '../components/common/SearchableDropdown';
import { US_STATES } from '../constants/address';
import { debounce } from '../utils/helpers';

const AddressCheckoutForm = ({
  setGoToCheckoutEnabled,
  setGoToCheckoutSubmitting,
  cartData,
  updateContact,
  setAddress,
  handleNextWizardStep,
  handleApiCartAndBackendUpdates,
}) => {
  const { apiFetchRequest } = useBackendApi();
  const { addAlertMessages, renderSnackBarAlertComponent } = useSnackBarAlertMessages();
  const [validAddressFields, setValidAddressFields] = useState(false);

  // const handlingValidAddressSubmission = async (values) => {
  //   console.log('data is submitted', values);
  // };

  const formik = useFormik({
    validateOnBlur: false,
    validateOnMount: false,
    validateOnChange: true,
    initialValues: {
      email: cartData.contact,
      address1: cartData.address.address1,
      address2: cartData.address.address2,
      deliveryInstructions: cartData.address.deliveryInstructions,
      city: cartData.address.city,
      state: cartData.address.state,
      zipcode: cartData.address.zipcode,
    },
    validate: (values) => {
      const errors = {};
      const zipcodeError = validateZipcode(values.zipcode);
      if (zipcodeError) {
        errors.zipcode = zipcodeError;
      }
      return errors;
    },
    validationSchema: orderAddressCheckoutSchema,
    // keeping this one for now in the case of us
    // wanting to validate details before submitting
    // onSubmit: handlingValidAddressSubmission,
  });

  const handleAddressValidation = useCallback(
    async (formikValues) => {
      const newAddress = {
        address1: formikValues.address1,
        address2: formikValues.address2,
        city: formikValues.city,
        state: formikValues.state,
        zipcode: formikValues.zipcode,
      };
      const validationResponse = await validateAddress(newAddress, apiFetchRequest);

      if (validationResponse === 'VALID') {
        setAddress(newAddress);
        updateContact(formikValues.email);
        handleApiCartAndBackendUpdates(newAddress, formikValues.email, 'AddressCheckoutForm');
      } else {
        setGoToCheckoutSubmitting(false);
        console.error('Address validation failed. Please check the address details.');
        addAlertMessages({
          message: 'Invalid Address. Please provide the correct address',
          severityType: 'error',
        });
      }
    },
    [formik.isValid, formik.isValidating, formik.values],
  );

  const handleAddressAndBackendUpdates = useCallback(
    async (allValues) => {
      const { formikValues, formikIsValid, formikIsValidating } = allValues;

      if (!formikIsValid) {
        return;
      }
      // here we will trigger the address validation and the other backend updates...
      const condition = !formikIsValidating && formikIsValid && formikValues.zipcode.length >= 5;
      if (condition) {
        setGoToCheckoutSubmitting(true);
        setValidAddressFields(true);
        handleAddressValidation(formikValues);
      } else {
        setValidAddressFields(false);
        setGoToCheckoutEnabled(false);
        setGoToCheckoutSubmitting(false);
      }
    },
    [handleAddressValidation, setGoToCheckoutEnabled, setGoToCheckoutSubmitting],
  );

  const debouncedHandleAddressAndBackendUpdates = useRef(debounce(handleAddressAndBackendUpdates, 1000)).current;

  useEffect(() => {
    if (!validAddressFields) return;

    // Once the user decides to change a valid address
    // we disable the go to checkout button right away
    setGoToCheckoutEnabled(false);
  }, [formik.values, validAddressFields]);

  useEffect(() => {
    if (!formik.isValid || formik.isValidating) return;

    const allValues = {
      formikValues: formik.values,
      formikIsValid: formik.isValid,
      formikIsValidating: formik.isValidating,
    };

    setGoToCheckoutEnabled(false);
    debouncedHandleAddressAndBackendUpdates(allValues);
  }, [formik.isValid, formik.isValidating, formik.values, debouncedHandleAddressAndBackendUpdates]);

  useEffect(() => {
    return () => debouncedHandleAddressAndBackendUpdates.cancel();
  }, [debouncedHandleAddressAndBackendUpdates]);

  return (
    <Grid key={'grid-container-address'} container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h6">Contact</Typography>
      </Grid>
      <Grid item xs={12}>
        <TextField
          key="email-or-phone-input"
          id="email-phone-contact"
          label="Email"
          required
          name="email"
          variant="outlined"
          fullWidth
          {...formik.getFieldProps('email')}
          error={formik.touched.email && Boolean(formik.errors.email)}
          helperText={formik.touched.email && formik.errors.email}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6">Delivery</Typography>
      </Grid>
      <Grid item xs={12}>
        <TextField
          key="address1"
          id="address1"
          label="Address"
          name="address1"
          required
          fullWidth
          {...formik.getFieldProps('address1')}
          error={formik.touched.address1 && Boolean(formik.errors.address1)}
          helperText={formik.touched.address1 && formik.errors.address1}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          key="address2"
          id="address2"
          label="Apartment, suite, etc. (optional)"
          name="address2"
          fullWidth
          {...formik.getFieldProps('address2')}
          error={formik.touched.address2 && Boolean(formik.errors.address2)}
          helperText={formik.touched.address2 && formik.errors.address2}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          key="city"
          id="city"
          label="City"
          name="city"
          required
          fullWidth
          {...formik.getFieldProps('city')}
          error={formik.touched.city && Boolean(formik.errors.city)}
          helperText={formik.touched.city && formik.errors.city}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <SearchableDropdown
          label="State"
          options={US_STATES}
          value={formik.values.state}
          {...formik.getFieldProps('state')}
          onChange={(selectedOption) => {
            formik.setFieldValue('state', selectedOption?.value || '');
          }}
          required
          error={formik.touched.state && Boolean(formik.errors.state)}
          helperText={formik.touched.state && formik.errors.state}
          disabled={false}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <TextField
          key="zipcode"
          id="zipcode"
          label="Zipcode"
          name="zipcode"
          required
          fullWidth
          {...formik.getFieldProps('zipcode')}
          error={formik.touched.zipcode && Boolean(formik.errors.zipcode)}
          helperText={formik.touched.zipcode && formik.errors.zipcode}
        />
      </Grid>
      <Grid item xs={12}>
        <Typography variant="h6">Delivery instructions</Typography>
      </Grid>
      <Grid item xs={12}>
        <TextField
          key="deliveryInstructions"
          id="deliveryInstructions"
          label="Delivery instructions (Optional)"
          name="deliveryInstructions"
          fullWidth
          {...formik.getFieldProps('deliveryInstructions')}
          error={formik.touched.deliveryInstructions && Boolean(formik.errors.deliveryInstructions)}
          helperText={formik.touched.deliveryInstructions && formik.errors.deliveryInstructions}
          multiline
        />
      </Grid>
      {renderSnackBarAlertComponent()}
    </Grid>
  );
};

export default AddressCheckoutForm;
